import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Stack,
  Chip,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getFirestore, query, where, orderBy, limit } from 'firebase/firestore';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import firebaseApp from '../utils/firebaseConfig';
import { matchesSearch } from '../utils/search';
import { formatPhoneDisplay } from '../utils/phone';

const db = getFirestore(firebaseApp);

// Página pedida por el cliente (video 2026-08-31, como el "Appointments" de
// Amelia): ver las reservaciones EN EL ORDEN EN QUE LOS CLIENTES LAS VAN
// HACIENDO — "hoy fulano reservó para el sábado" — incluyendo las que no han
// pagado, con buscador por nombre/teléfono.

const RANGE_OPTIONS = [
  { key: 'today', label: 'Hechas hoy', days: 1 },
  { key: '7d', label: 'Últimos 7 días', days: 7 },
  { key: '30d', label: 'Últimos 30 días', days: 30 },
  { key: 'all', label: 'Todas (últimas 500)', days: null },
];

const STATUS_META = {
  confirmed: { label: 'Confirmada', color: 'success' },
  pending_payment: { label: 'Pendiente de pago', color: 'warning' },
  cancelled: { label: 'Cancelada', color: 'error' },
};

const PAYMENT_META = {
  succeeded: { label: 'Pagada', color: 'success' },
  free: { label: 'Sin costo', color: 'info' },
  refunded: { label: 'Reembolsada', color: 'default' },
  pending: { label: 'Sin pagar', color: 'warning' },
};

const toDate = (ts) => {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isSameDay = (a, b) => a && b &&
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const formatCreated = (ts) => {
  const d = toDate(ts);
  if (!d) return '—';
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const time = d.toLocaleTimeString('es-PR', { hour: 'numeric', minute: '2-digit' });
  if (isSameDay(d, now)) return `Hoy ${time}`;
  if (isSameDay(d, yesterday)) return `Ayer ${time}`;
  return `${d.toLocaleDateString('es-PR', { month: 'short', day: 'numeric' })} ${time}`;
};

// r.date es 'YYYY-MM-DD' — se parsea como fecha local, no UTC
const formatForDate = (dateStr) => {
  if (!dateStr) return '—';
  const [y, m, d] = String(dateStr).split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('es-PR', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
};

const formatPrice = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function ReservationActivityPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rangeKey, setRangeKey] = useState('7d');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const range = RANGE_OPTIONS.find((r) => r.key === rangeKey) || RANGE_OPTIONS[1];
      const parts = [collection(db, 'reservations')];
      if (range.days) {
        const cutoff = new Date();
        if (range.key === 'today') cutoff.setHours(0, 0, 0, 0);
        else cutoff.setDate(cutoff.getDate() - range.days);
        parts.push(where('createdAt', '>=', cutoff));
      }
      parts.push(orderBy('createdAt', 'desc'));
      parts.push(limit(500));
      const snap = await getDocs(query(...parts));
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error cargando actividad de reservaciones:', e);
    }
    setLoading(false);
  };

  const filtered = useMemo(() => rows.filter((r) => matchesSearch(search, [
    r.userName, r.userPhone, r.userEmail, r.confirmationCode, r.categoryName, r.laneName,
  ])), [rows, search]);

  const todayCount = rows.filter((r) => isSameDay(toDate(r.createdAt), new Date())).length;

  return (
    <>
      <Helmet>
        <title>Reservaciones Recientes | BBTC Dashboard</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h4">Reservaciones Recientes</Typography>
          <Button variant="outlined" startIcon={<Iconify icon="eva:refresh-fill" />} onClick={fetchRows}>
            Refrescar
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          En el orden en que los clientes las van haciendo — incluye las que aún no han pagado.
          {todayCount > 0 ? ` Hoy se han hecho ${todayCount}.` : ''}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {RANGE_OPTIONS.map((r) => (
            <Chip
              key={r.key}
              label={r.label}
              color={rangeKey === r.key ? 'primary' : 'default'}
              variant={rangeKey === r.key ? 'filled' : 'outlined'}
              onClick={() => { setRangeKey(r.key); setPage(0); }}
            />
          ))}
        </Stack>

        <Card>
          <Stack sx={{ p: 2 }}>
            <TextField
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Buscar por nombre, teléfono, email o código..."
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Hecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Categoría</TableCell>
                    <TableCell>Para cuándo</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Pago</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && <TableSkeleton rows={8} columns={7} />}
                  {!loading && filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r) => {
                    const st = STATUS_META[r.status] || { label: r.status || '—', color: 'default' };
                    const pay = PAYMENT_META[r.paymentStatus] || { label: r.paymentStatus || '—', color: 'default' };
                    return (
                      <TableRow
                        hover
                        key={r.id}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/dashboard/reservations/details?id=${r.id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={isSameDay(toDate(r.createdAt), new Date()) ? 700 : 400}>
                            {formatCreated(r.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">{r.userName || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatPhoneDisplay(r.userPhone)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{r.categoryName || '—'}</Typography>
                          {r.laneName && (
                            <Typography variant="caption" color="text.secondary">{r.laneName}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatForDate(r.date)}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.startTime || ''}</Typography>
                        </TableCell>
                        <TableCell>{formatPrice(r.amountCents)}</TableCell>
                        <TableCell><Chip label={st.label} color={st.color} size="small" /></TableCell>
                        <TableCell><Chip label={pay.label} color={pay.color} size="small" variant="outlined" /></TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && filtered.length === 0 && (
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {search ? 'Nada encontrado con esa búsqueda.' : 'No hay reservaciones en este período.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
          />
        </Card>
      </Container>
    </>
  );
}
